import { useEffect } from 'react'
import { useConstAndReimbursement } from 'src/mtska-frontend-app-component'

export const useFieldWatcher = ({ setValue, watchedFields }) => {

    const {
        contractReajustmentType,
        contractDuration,
        start_at,
        totalLeaseFee,
        actualMonthlyMileage,
        projectedCostOrReimbursement,
        contractReajustmentStart_at,
        contractMileage,
        projectedMileage,
        bracket1ExtraKmCost,
        bracket1KmReimbursement,
        bracket1Percentage,
        bracket2ExtraKmCost,
        bracket2KmReimbursement,
        bracket2Percentage,
        bracket3ExtraKmCost,
        bracket3KmReimbursement,
        bracket3Percentage,

        contractKmDeductible,
        initialMileage,
        maxReimbursementAmount,

        originalTotalLeaseFee,
        contractDurationEnlapsed,
        contractDurationRemaining,
        totalCostOwnershipFee,
        actualTotalCostOwnershipFee
    } = watchedFields

    useEndAt({ setValue, watchedFields: { contractDuration, start_at } })
    useLifetimeLeaseFee({ setValue, watchedFields: { contractReajustmentType, contractDuration, contractDurationEnlapsed, contractDurationRemaining, originalTotalLeaseFee, totalLeaseFee } })
    useTotalCostOwnershipFee({ setValue, watchedFields: { contractReajustmentType, contractDuration, contractDurationEnlapsed, contractDurationRemaining, originalTotalLeaseFee, totalLeaseFee, projectedCostOrReimbursement } })
    useProjectedMileage({ setValue, watchedFields: { actualMonthlyMileage, contractDuration } })
    useProjectedMileageCost({ setValue, watchedFields: { contractMileage, projectedMileage, bracket1ExtraKmCost, bracket1KmReimbursement, bracket1Percentage, bracket2ExtraKmCost, bracket2KmReimbursement, bracket2Percentage, bracket3ExtraKmCost, bracket3KmReimbursement, bracket3Percentage, contractKmDeductible, initialMileage, maxReimbursementAmount } })
    useEnlapsedRemaining({ setValue, watchedFields: { contractDuration, start_at, contractReajustmentStart_at } })
    useContractReajustmentSettlement({ setValue, watchedFields: { contractReajustmentType, originalTotalLeaseFee, totalLeaseFee, contractDurationEnlapsed } })
    useRecalculationsaving({ setValue, watchedFields: { totalCostOwnershipFee, actualTotalCostOwnershipFee } })
}

const useEndAt = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { contractDuration, start_at } = watchedFields

        if (start_at && contractDuration) {
            let endDate = new Date(start_at);
            endDate.setMonth(endDate.getMonth() + parseInt(contractDuration));
            endDate.setDate(endDate.getDate() - 1);
            if (isNaN(endDate.getTime())) {
                setValue('end_at', 0);
            }
            setValue('end_at', endDate.toISOString());
        }

    }, [...Object.values(watchedFields)])
}

const useProjectedMileage = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { actualMonthlyMileage, contractDuration } = watchedFields

        setValue('projectedMileage', parseInt(actualMonthlyMileage * contractDuration));

    }, [...Object.values(watchedFields)])
}

const useRecalculationsaving = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { totalCostOwnershipFee, actualTotalCostOwnershipFee } = watchedFields

        setValue('recalculationsaving', parseFloat((actualTotalCostOwnershipFee - totalCostOwnershipFee).toFixed(2)) * -1);

    }, [...Object.values(watchedFields)])
}

const useProjectedMileageCost = ({ setValue, watchedFields }) => {
    const { calculate, calculateDelta } = useConstAndReimbursement();

    useEffect(() => {
        const { contractMileage, projectedMileage, bracket1ExtraKmCost, bracket1KmReimbursement, bracket1Percentage, bracket2ExtraKmCost, bracket2KmReimbursement, bracket2Percentage, bracket3ExtraKmCost, bracket3KmReimbursement, bracket3Percentage, contractKmDeductible, initialMileage, maxReimbursementAmount } = watchedFields

        const costOrReimbursement = calculate(
            contractMileage,
            projectedMileage,
            bracket1ExtraKmCost,
            bracket1KmReimbursement,
            bracket1Percentage,
            bracket2ExtraKmCost,
            bracket2KmReimbursement,
            bracket2Percentage,
            bracket3ExtraKmCost,
            bracket3KmReimbursement,
            bracket3Percentage,
            contractKmDeductible,
            initialMileage,
            maxReimbursementAmount
        );

        setValue('projectedCostOrReimbursement', parseFloat(costOrReimbursement).toFixed(2));

        const delta = calculateDelta(
            contractMileage,
            projectedMileage,
            contractKmDeductible,
            initialMileage
        )

        setValue('projectedDeltaMileage', delta);

    }, [...Object.values(watchedFields)])
}

const useContractReajustmentSettlement = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { contractReajustmentType, originalTotalLeaseFee, totalLeaseFee, contractDurationEnlapsed } = watchedFields

        if (contractReajustmentType == "adjustment_with_settlement" && originalTotalLeaseFee && totalLeaseFee && contractDurationEnlapsed) {
            setValue('contractReajustmentSettlement', (totalLeaseFee - originalTotalLeaseFee) * contractDurationEnlapsed);
        } else {
            setValue('contractReajustmentSettlement', 0)
        }

    }, [...Object.values(watchedFields)])
}

const useLifetimeLeaseFee = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { contractReajustmentType, contractDuration, contractDurationEnlapsed, contractDurationRemaining, originalTotalLeaseFee, totalLeaseFee } = watchedFields;

        const cd = parseInt(contractDuration);

        if (contractReajustmentType == "adjustment_without_settlement" && contractDurationRemaining && totalLeaseFee) {
            setValue('lifetimeLeaseFee', parseFloat((contractDurationEnlapsed * originalTotalLeaseFee) + (contractDurationRemaining * totalLeaseFee)).toFixed(2));
        } else if (cd && totalLeaseFee) {
            setValue('lifetimeLeaseFee', parseFloat(cd * totalLeaseFee).toFixed(2));
        }

    }, [...Object.values(watchedFields)])
}

const useTotalCostOwnershipFee = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { contractReajustmentType, contractDuration, contractDurationEnlapsed, contractDurationRemaining, originalTotalLeaseFee, totalLeaseFee, projectedCostOrReimbursement } = watchedFields;

        const cd = parseInt(contractDuration);
        const pcor = parseFloat(projectedCostOrReimbursement);

        if (contractReajustmentType == "adjustment_without_settlement" && contractDurationRemaining && totalLeaseFee) {
            setValue('totalCostOwnershipFee', parseFloat((contractDurationEnlapsed * originalTotalLeaseFee) + (contractDurationRemaining * totalLeaseFee) + (pcor ?? 0)).toFixed(2));
        } else if (cd && totalLeaseFee) {
            setValue('totalCostOwnershipFee', parseFloat(cd * totalLeaseFee + (pcor ?? 0)).toFixed(2));
        }

    }, [...Object.values(watchedFields)])
}


const useEnlapsedRemaining = ({ setValue, watchedFields }) => {
    useEffect(() => {
        const { contractDuration, start_at, contractReajustmentStart_at } = watchedFields

        if (start_at && contractDuration && contractReajustmentStart_at) {
            let start_at_date = new Date(start_at);
            let end_at_date = new Date(start_at);
            end_at_date.setMonth(end_at_date.getMonth() + parseInt(contractDuration));
            end_at_date.setDate(end_at_date.getDate() - 1);

            let contractReajustmentStart_at_date = new Date(contractReajustmentStart_at);

            const millisecondsInMonth = 30.436875 * 24 * 60 * 60 * 1000; // Media di giorni in un mese

            const enlapsed = (contractReajustmentStart_at_date - start_at_date) / millisecondsInMonth;
            setValue('contractDurationEnlapsed', parseFloat(enlapsed.toFixed(1)));

            const remaining = (end_at_date - contractReajustmentStart_at_date) / millisecondsInMonth;
            setValue('contractDurationRemaining', parseFloat(remaining.toFixed(1)));
        }

    }, [...Object.values(watchedFields)])
}